/**
 * Attempt telemetry: section timings (FIDMAT-BL-06).
 *
 * Ported from the vanilla client deliberately unchanged in behaviour, because
 * the analysis that depends on it is already live: the Engagement view's
 * drop-off funnel reads `lastPageName` and per-section `totalMs`.
 *
 * STRICTLY FIRE-AND-FORGET. A respondent must never see an error, lose an
 * answer, or be blocked from continuing because telemetry could not be
 * written. Every failure path here is swallowed.
 *
 * The buffer is cleared only once a flush SUCCEEDS, so a failed send is
 * retried on the next transition rather than dropped. Resends are safe: the
 * server merges page events on their timestamp, so the same visit reported
 * twice does not double-count.
 *
 * Module-level rather than React state on purpose. This is not render state,
 * nothing displays it, and putting it in a component would make the timings
 * sensitive to re-render behaviour that has nothing to do with the survey.
 */

import { api, getToken } from './api.js';

let buffer = [];
let enteredAt = null;
let enteredName = null;
let enteredIndex = null;

export function markPageEntered(name, index) {
  enteredAt = Date.now();
  enteredName = name;
  enteredIndex = index ?? null;
}

/** Close off the current section and add it to the buffer. */
export function capturePageExit() {
  if (!enteredAt || !enteredName) return;
  const now = Date.now();
  buffer.push({
    name: enteredName,
    index: enteredIndex,
    enteredAt: new Date(enteredAt).toISOString(),
    leftAt: new Date(now).toISOString(),
    durationMs: now - enteredAt,
  });
  enteredAt = null;
  enteredName = null;
}

/** Send buffered timings. Never throws, never blocks the caller. */
export function flushPageEvents() {
  if (!buffer.length || !getToken()) return;
  const batch = buffer.slice();
  api('/api/attempt/pages', { method: 'POST', body: { pages: batch } })
    .then(() => { buffer = buffer.slice(batch.length); })
    .catch(() => { /* keep the buffer; retried on the next transition */ });
}

/**
 * The buffer, for the submit call.
 *
 * The final section's timing has to ride along with the submission: nothing
 * navigates away from the last page, so a separate flush would never happen
 * and the longest section would be missing from every completed attempt.
 */
export const pendingPageEvents = () => buffer.slice();
export const clearPageEvents = () => { buffer = []; };

/** Sign-out / restart: drop everything so a new attempt starts clean. */
export function resetTelemetry() {
  buffer = [];
  enteredAt = null;
  enteredName = null;
  enteredIndex = null;
}
